<template>
	<div class="activos_tab report_theme_container" data-theme="activos">
		<TabHeader
			title="Activos"
			description="Analiza la tendencia y composición de tus activos"
			:filterPeriod="filterPeriod"
			:filterOptions="filterOptions"
			:endMonth="endMonth"
			:getNow="getNow"
			@update-filter-period="updateFilterPeriod"
			@update-end-month="updateEndMonth">
			<template #center-sub-nav>
				<div class="filters_row is-flex is-align-items-center">
					<div class="sub_nav_filters">
						<button type="button"
							class="filter_btn"
							:class="{ 'is-active': isCuentasMode }"
							@click.stop="setCuentasMode">
							Cuentas
						</button>
					</div>

					<MultiSelectDropdown
						:value="selectedCounterpartTypes"
						:options="counterpartTypeOptions"
						placeholder="Tipos de contraparte"
						@input="SET_SELECTED_COUNTERPART_TYPES" />
				</div>
			</template>
		</TabHeader>

		<!-- ================================================================ -->
		<!-- Summary Cards Section                                             -->
		<!-- A diferencia de Costos/Gastos, Activos no usa el slot #extra: el  -->
		<!-- diseño no muestra una "conclusión" inline (ni Margen Bruto ni     -->
		<!-- Eficiencia). Mantener el slot vacío respeta la regla del sistema. -->
		<!-- Se pliega vía CollapsibleSection; storageKey global sincroniza el -->
		<!-- estado entre todos los tabs de finanzas.                          -->
		<!-- ================================================================ -->
		<CollapsibleSection class="mt-1"
			title="Resumen"
			storageKey="financialReports:summary-expanded">
			<template v-if="summaryHint" #hint>{{ summaryHint }}</template>
			<div class="cards_container is-flex">
				<CardVentas v-for="(card, index) in displayCards" :key="index" v-bind="card" class="mr-4" />
			</div>
		</CollapsibleSection>

		<!-- Main Table Section -->
		<ExpensesEvolutionTable class="mt-2"
			title="Evolución de Activos"
			emptyMessage="No hay datos de activos para el período seleccionado."
			:monthHeaders="monthHeaders"
			:visibleRows="visibleRows"
			:totalRow="totalRow"
			:ratioRow="ratioRow"
			:expandedL2Rows="expandedL2Rows"
			:isLoading="isLoading"
			:error="error"
			:selectedPeriod="selectedPeriodRange"
			:periodOptions="periodOptions"
			:filterPeriod="filterPeriod"
			:showAccumulated="true"
			@toggle-l2="toggleL2"
			@change-period="setPeriodRange"
			@retry="fetchAssetsData" />
	</div>
</template>

<script>
import moment from "moment";
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import MultiSelectDropdown from "@/components/generics/MultiSelectDropdown.vue";
import TabHeader from "@/components/financialReports/TabHeader.vue";
import CollapsibleSection from "@/components/financialReports/CollapsibleSection.vue";
import CardVentas from "@/components/financialReports/CardVentas.vue";
import ExpensesEvolutionTable from "@/components/financialReports/ExpensesEvolutionTable.vue";
import {
	PERIOD_TYPES,
	ASSET_COUNTERPART_TYPES,
	ASSET_CARD_PROGRESS_COLORS,
	FILTER_PERIOD_OPTIONS,
} from "@/config/reportStructure";
import { formatPercent, formatRangeLabel } from "@/views/financialReports/utils/periodFormatting";
import { emptyCard } from "@/views/financialReports/utils/summaryCards";
import { parseToMoney } from "@/utils/currency";
import { notificate } from "@/utils/message";

const EMPTY_CARD = () => emptyCard();

export default {
	name: "ActivosTab",

	components: {
		TabHeader,
		CollapsibleSection,
		MultiSelectDropdown,
		CardVentas,
		ExpensesEvolutionTable,
	},

	data() {
		return {
			periodOptions: PERIOD_TYPES,
			counterpartTypeOptions: ASSET_COUNTERPART_TYPES,
			expandedL2Rows: [],
			filterOptions: FILTER_PERIOD_OPTIONS,
		};
	},

	computed: {
		...mapState("reportsFilters", ["endMonth", "selectedPeriodRange"]),
		...mapState("financialReports", ["filterPeriod"]),
		...mapState("assetsReport", [
			"selectedCounterpartTypes",
			"monthHeaders",
			"isLoading",
			"error",
			"summaryCards",
			"summaryError",
		]),
		...mapGetters("assetsReport", ["tableRows", "totalRow", "ratioRow", "isCuentasMode"]),

		getNow() {
			return moment().toDate();
		},

		// Hint compacto al lado del label "Resumen" cuando la sección está
		// plegada — preserva el monto del mes actual para no perder contexto.
		summaryHint() {
			const amount = this.summaryCards && this.summaryCards.current_month
				? this.summaryCards.current_month.amount
				: null;
			return amount != null ? parseToMoney(amount) : "";
		},

		visibleRows() {
			return this.tableRows.filter(row => {
				if (row.level === 2) return true;
				return this.expandedL2Rows.includes(row.parent);
			});
		},

		displayCards() {
			if (!this.summaryCards) {
				return [EMPTY_CARD(), EMPTY_CARD(), EMPTY_CARD()];
			}
			const { current_month, ytd, ltm } = this.summaryCards;
			return [
				this.buildCard(current_month, { showMom: true }),
				this.buildCard(ytd),
				this.buildCard(ltm),
			];
		},

		fetchSignature() {
			return [
				this.endMonth,
				this.selectedPeriodRange,
				this.selectedCounterpartTypes.join(","),
				this.filterPeriod,
			].join("|");
		},

		summarySignature() {
			return moment(this.endMonth).format("YYYY-MM");
		},
	},

	methods: {
		...mapActions("assetsReport", ["fetchAssetsData", "fetchAssetsSummary"]),
		...mapActions("reportsFilters", ["setPeriodRange"]),
		...mapMutations("reportsFilters", ["SET_END_MONTH"]),
		...mapMutations("assetsReport", [
			"SET_SELECTED_COUNTERPART_TYPES",
		]),
		...mapMutations("financialReports", ["SET_FILTER_PERIOD"]),

		updateEndMonth(date) {
			this.SET_END_MONTH(date);
		},
		updateFilterPeriod(period) {
			this.SET_FILTER_PERIOD(period);
		},
		setCuentasMode() {
			this.SET_SELECTED_COUNTERPART_TYPES([]);
		},
		toggleL2(label) {
			const index = this.expandedL2Rows.indexOf(label);
			if (index > -1) {
				this.expandedL2Rows.splice(index, 1);
			} else {
				this.expandedL2Rows.push(label);
			}
		},

		buildCard(card, { showMom = false } = {}) {
			// Activos no muestra breakdown en la imagen original, pero el contrato
			// del backend permite traerlo en el futuro. Por ahora reconstruimos
			// los progress items desde los level_2 acumulados — si están vacíos,
			// la card simplemente no muestra barras.
			// `showMom` sólo aplica al mes actual: YTD/LTM no muestran badge MoM
			// (mismo criterio que NoOperTab/RentabilidadTab).
			const progressItems = (card.breakdown || []).map((item, i) => ({
				label: item.label,
				percent: item.percent,
				color: ASSET_CARD_PROGRESS_COLORS[i % ASSET_CARD_PROGRESS_COLORS.length],
			}));
			return {
				period: formatRangeLabel(card.period_start, card.period_end),
				amount: card.amount,
				mom: showMom ? formatPercent(card.mom) : "",
				yoy: formatPercent(card.yoy),
				progressItems,
			};
		},
	},

	mounted() {
		this.fetchAssetsData();
		this.fetchAssetsSummary();
	},

	watch: {
		fetchSignature() { this.fetchAssetsData(); },
		summarySignature() { this.fetchAssetsSummary(); },
		// El fetch del resumen falla en silencio (las cards quedan vacías). Un
		// toast hace visible el error sin romper el layout de las cards.
		summaryError(message) {
			if (message) notificate(message, "is-danger is-light", "is-top-right");
		},
	},
};
</script>

<style scoped lang="scss">
.activos_tab {
	position: relative;
	padding-bottom: 40px;
	height: 100%;
}

// Header común vive en TabHeader.vue. Replicamos las classes del slot
// #center-sub-nav (botón Cuentas + MultiSelectDropdown) localmente porque
// TabHeader es scoped y sus estilos no llegan al markup inyectado al slot.

.filters_row {
	gap: 12px;
}

.sub_nav_filters {
	display: inline-flex;
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	width: fit-content;
	box-sizing: border-box;
	min-height: 34px;
}

.filter_btn {
	display: inline-flex;
	align-items: center;
	padding: 0 16px;
	min-height: 32px;
	border: none;
	background: transparent;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	line-height: 1;
	color: var(--report-text-muted-strong);
	cursor: pointer;
	transition: all 0.15s ease;
	box-sizing: border-box;

	&:hover { background: var(--report-surface); }

	&.is-active {
		background: var(--report-primary);
		color: var(--report-bg);
	}
}

.cards_container {
	width: 100%;
}
</style>
